import React, { useState, useEffect, useRef, useMemo } from 'react';
import '../components/Services.css';
import '../components/Contact.css';

// Newsletter data - Easy to add more newsletters here
const allNewsletters = [
    {
      id: 1,
      title: '[Newsletter Title]',
      date: '2024-12-01',
      month: 'December',
      year: 2024,
      description: '[Brief description of the newsletter content, topics covered, and key highlights.]',
      pdf: '/newsletters/newsletter-dec-2024.pdf', // Optional PDF link
      tags: ['Research', 'Aging', 'Community']
    },
    {
      id: 2,
      title: '[Newsletter Title]',
      date: '2024-11-01',
      month: 'November',
      year: 2024,
      description: '[Brief description of the newsletter content, topics covered, and key highlights.]',
      pdf: '/newsletters/newsletter-nov-2024.pdf',
      tags: ['Research', 'Interviews', 'Awareness']
    },
    {
      id: 3,
      title: '[Newsletter Title]',
      date: '2024-10-01',
      month: 'October',
      year: 2024,
      description: '[Brief description of the newsletter content, topics covered, and key highlights.]',
      pdf: '/newsletters/newsletter-oct-2024.pdf',
      tags: ['Research', 'Stories', 'Community']
    }
];

const NewsletterArchivePage = () => {
  const [newsletters, setNewsletters] = useState([]);
  const [selectedYear, setSelectedYear] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  useEffect(() => {
    let filtered = [...allNewsletters];

    // Filter by year
    if (selectedYear !== 'All') {
      filtered = filtered.filter(nl => nl.year === parseInt(selectedYear));
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(nl => 
        nl.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        nl.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        nl.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Sort by date (newest first)
    filtered.sort((a, b) => new Date(b.date) - new Date(a.date));

    setNewsletters(filtered);
  }, [selectedYear, searchTerm]);

  // Get unique years from newsletters
  const years = useMemo(() => {
    return ['All', ...Array.from(new Set(allNewsletters.map(nl => nl.year))).sort((a, b) => b - a)];
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <div className="page-container">
      <section className="services section" ref={sectionRef}>
        <div className="container">
          <div className={`services-header ${isVisible ? 'animate-fade-in' : ''}`}>
            <h1 className="section-title">Research Newsletter Archive</h1>
            <p className="section-subtitle">
              Browse our monthly newsletters featuring research findings, interviews, insights, and stories about aging and intergenerational connections.
            </p>
          </div>

          {/* Search and Filter Controls */}
          <div className={`newsletter-controls ${isVisible ? 'animate-fade-in' : ''}`}>
            <div className="newsletter-search">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
                <path d="M21 21L16.65 16.65" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              <input
                type="text"
                placeholder="Search newsletters by title, description, or tags..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="newsletter-search-input"
              />
            </div>

            <div className="newsletter-year-filter">
              <label>Filter by Year:</label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="newsletter-year-select"
              >
                {years.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Results Count */}
          <div className="newsletter-results-count">
            <p>Showing {newsletters.length} newsletter{newsletters.length !== 1 ? 's' : ''}</p>
          </div>

          {/* Newsletter Grid */}
          {newsletters.length > 0 ? (
            <div className="newsletter-archive-grid">
              {newsletters.map((newsletter, index) => (
                <div
                  key={newsletter.id}
                  className={`newsletter-archive-card ${isVisible ? 'animate-fade-in' : ''}`}
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <div className="newsletter-card-header">
                    <div className="newsletter-date-badge">
                      <span className="newsletter-month">{newsletter.month}</span>
                      <span className="newsletter-year">{newsletter.year}</span>
                    </div>
                    <div className="newsletter-date-full">
                      {formatDate(newsletter.date)}
                    </div>
                  </div>

                  <div className="newsletter-card-content">
                    <h3 className="newsletter-card-title">{newsletter.title}</h3>
                    <p className="newsletter-card-description">{newsletter.description}</p>
                    
                    <div className="newsletter-tags">
                      {newsletter.tags.map((tag, tagIndex) => (
                        <span key={tagIndex} className="newsletter-tag">{tag}</span>
                      ))}
                    </div>
                  </div>

                  <div className="newsletter-card-footer">
                    <button className="btn btn-primary newsletter-read-btn">
                      Read Newsletter
                    </button>
                    {newsletter.pdf && (
                      <a 
                        href={newsletter.pdf} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="newsletter-pdf-link"
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        PDF
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-newsletters-message">
              <p>No newsletters found matching your search criteria.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default NewsletterArchivePage;

